import { useCallback, useEffect, useState } from 'react';
import { execApi } from '../../../services/api';

// The month-close bar: where a month stands, and the button that freezes it.
//
// Proactive, not a chore to remember — when a closed-and-ready month is sitting
// there, this says so. But the freeze is a human gate: payroll shouldn't close
// itself. Closing snapshots the month; from then on it reads frozen, never live.

interface Status {
  month: string;
  status: 'open' | 'sent' | 'closed';
  therapists: number;
  approved_or_timed_out: number;
  pending: number;
  can_close: boolean;
}

export function MonthCloseBar({ month, onClosed }: { month: string; onClosed: () => void }) {
  const [st, setSt] = useState<Status | null>(null);
  const [closing, setClosing] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await execApi.getSalaryMonthStatus(month);
      setSt(r.data);
    } catch {
      setSt(null);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const close = async () => {
    if (!window.confirm(
      `לסגור את ${month}? החודש ייקפא — התלושים והתעריפים יישמרו כסנאפשוט ` +
      `ולא ישתנו יותר. לא ניתן לערוך חודש סגור.`)) return;
    setClosing(true);
    try {
      await execApi.closeSalaryMonth(month);
      await load();
      onClosed();
    } catch (e: any) {
      alert(e?.response?.data?.detail ?? 'הסגירה נכשלה');
    } finally {
      setClosing(false);
    }
  };

  if (!st) return null;

  if (st.status === 'closed') {
    return (
      <div className="rounded-lg px-4 py-3 text-sm bg-gray-100 text-gray-700 border border-gray-200 flex items-center gap-2">
        <span className="text-gray-500">🔒</span>
        <b>החודש סגור.</b> המספרים מוצגים מהסנאפשוט הקפוא — לא מחושבים מחדש.
      </div>
    );
  }

  return (
    <div className="rounded-lg px-4 py-3 bg-blue-50 border border-blue-100 flex items-center justify-between gap-4 flex-wrap">
      <div className="text-sm text-blue-900">
        <b>סגירת חודש.</b>{' '}
        {st.approved_or_timed_out} מתוך {st.therapists} אושרו או חלף חלון 5 הימים
        {st.pending > 0 && <span className="text-blue-700"> · {st.pending} ממתינים</span>}
      </div>
      <button
        onClick={close}
        disabled={!st.can_close || closing}
        title={st.can_close ? '' : (st.pending > 0
          ? 'יש מטפלים שטרם אישרו וטרם חלף חלון 5 הימים'
          : 'יש מטפלים ללא תעריף')}
        className="text-sm rounded-md px-4 py-1.5 font-medium disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed bg-blue-600 text-white hover:bg-blue-700"
      >
        {closing ? 'סוגר…' : 'סגור חודש וקפא סנאפשוט'}
      </button>
    </div>
  );
}
